import { useTranslation } from "react-i18next";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, CheckCircle2 } from "lucide-react";
import { SEO } from "@/components/SEO";
import { RevealText } from "@/components/shared/RevealText";
import { SectionReveal } from "@/components/shared/SectionReveal";
import { USE_CASE_IMAGES, USE_CASE_SLUGS } from "@/data/images";
import { DownloadButton } from "@/components/shared/DownloadButton";

export default function UseCaseDetail() {
  const { t } = useTranslation();
  const { lang = "fr", slug } = useParams();

  if (!USE_CASE_SLUGS.includes(slug)) return <Navigate to={`/${lang}`} replace />;

  const item = t(`use_cases.items.${slug}`, { returnObjects: true });
  const image = USE_CASE_IMAGES[slug];

  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Fastority", item: `${window.location.origin}/${lang}` },
      { "@type": "ListItem", position: 2, name: item.title, item: window.location.href },
    ],
  };

  return (
    <div data-testid="usecase-detail-page">
      <SEO title={`${item.title} — Fastority`} description={item.intro} image={image} path={`use-cases/${slug}`} schema={schema} />

      <section className="relative bg-slate900 text-white overflow-hidden">
        <img src={image} alt={item.hero_image_alt} className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate900 via-slate900/70 to-slate900/30" />
        <div className="relative max-w-4xl mx-auto px-5 sm:px-8 py-28 sm:py-36">
          <Link to={`/${lang}`} data-testid="usecase-back-home" className="inline-flex items-center gap-2 text-white/60 text-sm mb-8 hover:text-white transition-colors">
            <ArrowLeft size={15} /> {t("common.back")}
          </Link>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter">
            <RevealText lines={[item.title]} />
          </h1>
          <p className="mt-5 text-lg text-white/70 max-w-lg">{item.subtitle}</p>
        </div>
      </section>

      <section className="py-20 bg-offwhite">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <SectionReveal>
            <p className="text-lg text-slate-700 leading-relaxed">{item.intro}</p>
          </SectionReveal>

          <div className="mt-14 space-y-8">
            {item.sections.map((s, i) => (
              <SectionReveal key={s.title} delay={i * 0.08}>
                <div data-testid={`usecase-section-${i}`} className="flex gap-5 bg-white rounded-3xl p-7 shadow-[0_8px_30px_rgba(30,58,138,0.06)] border border-gray-100">
                  <div className="shrink-0 w-10 h-10 rounded-full bg-deepblue text-white flex items-center justify-center font-heading font-bold">
                    {i + 1}
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-xl text-slate900 mb-2">{s.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              </SectionReveal>
            ))}
          </div>

          <SectionReveal className="mt-16 text-center bg-deepblue rounded-3xl p-10 text-white grain-overlay relative overflow-hidden">
            <CheckCircle2 className="mx-auto mb-4 text-emeraldgreen" size={32} />
            <p className="font-heading font-bold text-2xl mb-6 whitespace-pre-line">{item.cta}</p>
            <DownloadButton testId="usecase-cta-download" className="inline-flex items-center gap-2 bg-coral text-white font-semibold px-7 py-4 rounded-full hover:scale-[1.02] transition-transform duration-300">
              {t("home.hero.cta_primary")} <ArrowRight size={18} />
            </DownloadButton>
          </SectionReveal>
        </div>
      </section>
    </div>
  );
}
